import { niceStep } from "@/lib/tool-kit/math/plot";

/**
 * Exponential growth and decay.
 *
 *   discrete:    A = P · (1 + r)^t     r is the rate per period, negative for decay
 *   continuous:  A = P · e^(k·t)       k is the continuous rate, negative for decay
 *
 * Any one of the final amount A, initial amount P, rate and time can be solved from the
 * other three. The per-period factor, the doubling (or halving) time and the rules of
 * 72, 70 and 69.3 come out of the rate once it is known.
 */
export type GrowthModel = "discrete" | "continuous";
export type GrowthDirection = "growth" | "decay";
export type GrowthUnknown = "final" | "initial" | "rate" | "time";

export interface GrowthInput {
  model: GrowthModel;
  direction: GrowthDirection;
  solve: GrowthUnknown;
  initial: number | null;
  final: number | null;
  /** Percent per period, as typed: 5 means 5%. Always the size of the rate. */
  ratePercent: number | null;
  time: number | null;
}

export type GrowthError =
  | "missing"
  | "initial-positive"
  | "final-positive"
  | "rate-negative"
  | "decay-too-large"
  | "rate-zero"
  | "time-zero"
  | "time-negative"
  | "overflow";

export interface RuleRow {
  rule: "exact" | "72" | "70" | "69.3";
  time: number;
  /** Signed percent difference from the exact time. */
  errorPercent: number;
}

export interface GrowthResult {
  model: GrowthModel;
  initial: number;
  final: number;
  time: number;
  /** Signed rate per period as a fraction: +0.05 growth, −0.05 decay (discrete: b − 1; continuous: k). */
  rate: number;
  /** The multiplier for one period. */
  factor: number;
  /** The same growth written as a discrete rate per period. */
  discreteRate: number;
  /** The same growth written as a continuous rate. */
  continuousRate: number;
  /** Time to double (growth) or halve (decay); null when nothing changes. */
  characteristic: { kind: "doubling" | "halving"; time: number } | null;
  rules: RuleRow[];
  totalChangePercent: number;
}

const LN2 = Math.log(2);

export type GrowthOutcome =
  | { ok: true; result: GrowthResult }
  | { ok: false; error: GrowthError };

function signedRate(input: GrowthInput): number {
  const size = (input.ratePercent ?? 0) / 100;
  return input.direction === "decay" ? -size : size;
}

/** The per-period multiplier for a signed rate. */
function factorOf(model: GrowthModel, rate: number): number {
  return model === "discrete" ? 1 + rate : Math.exp(rate);
}

function checkInputs(input: GrowthInput): GrowthError | null {
  const needs = {
    final: ["initial", "ratePercent", "time"],
    initial: ["final", "ratePercent", "time"],
    rate: ["initial", "final", "time"],
    time: ["initial", "final", "ratePercent"],
  } as const;
  for (const key of needs[input.solve]) {
    const value = input[key];
    if (value === null || !Number.isFinite(value)) {
      return "missing";
    }
  }
  if (input.solve !== "initial" && (input.initial ?? 1) <= 0) {
    return "initial-positive";
  }
  if (input.solve !== "final" && (input.final ?? 1) <= 0) {
    return "final-positive";
  }
  if (input.solve !== "rate" && (input.ratePercent ?? 0) < 0) {
    return "rate-negative";
  }
  if (
    input.solve !== "rate" &&
    input.model === "discrete" &&
    input.direction === "decay" &&
    (input.ratePercent ?? 0) >= 100
  ) {
    return "decay-too-large";
  }
  if (input.solve !== "time" && (input.time ?? 0) < 0) {
    return "time-negative";
  }
  return null;
}

function solveInitial(input: GrowthInput, factor: number): number | null {
  const power = factor ** (input.time as number);
  return Number.isFinite(power) && power > 0
    ? (input.final as number) / power
    : null;
}

function solveFinalAmount(input: GrowthInput, factor: number): number {
  return (input.initial as number) * factor ** (input.time as number);
}

export function characteristicTime(
  model: GrowthModel,
  rate: number
): { kind: "doubling" | "halving"; time: number } | null {
  const factor = factorOf(model, rate);
  if (factor === 1 || factor <= 0) {
    return null;
  }
  const log = Math.log(factor);
  return log > 0
    ? { kind: "doubling", time: LN2 / log }
    : { kind: "halving", time: LN2 / -log };
}

function rulesFor(model: GrowthModel, rate: number): RuleRow[] {
  const exact = characteristicTime(model, rate);
  const percent = Math.abs(rate) * 100;
  if (!exact || percent === 0) {
    return [];
  }
  const rows: RuleRow[] = [
    { rule: "exact", time: exact.time, errorPercent: 0 },
  ];
  for (const [rule, constant] of [
    ["72", 72],
    ["70", 70],
    ["69.3", 69.3],
  ] as const) {
    const time = constant / percent;
    rows.push({
      rule,
      time,
      errorPercent: ((time - exact.time) / exact.time) * 100,
    });
  }
  return rows;
}

/** Solve for the chosen unknown and derive everything else. */
export function solveGrowth(input: GrowthInput): GrowthOutcome {
  const problem = checkInputs(input);
  if (problem) {
    return { ok: false, error: problem };
  }
  let initial = input.initial as number;
  let final = input.final as number;
  let time = input.time as number;
  let rate = signedRate(input);
  const factor = factorOf(input.model, rate);
  if (input.solve === "final") {
    final = solveFinalAmount(input, factor);
  } else if (input.solve === "initial") {
    const solved = solveInitial(input, factor);
    if (solved === null) {
      return { ok: false, error: "overflow" };
    }
    initial = solved;
  } else if (input.solve === "rate") {
    if (time === 0) {
      return { ok: false, error: "time-zero" };
    }
    const ratio = final / initial;
    rate =
      input.model === "discrete"
        ? ratio ** (1 / time) - 1
        : Math.log(ratio) / time;
  } else {
    if (factor === 1 || rate === 0) {
      return { ok: false, error: "rate-zero" };
    }
    time = Math.log(final / initial) / Math.log(factor);
  }
  if (![initial, final, time, rate].every(Number.isFinite)) {
    return { ok: false, error: "overflow" };
  }
  const multiplier = factorOf(input.model, rate);
  return {
    ok: true,
    result: {
      model: input.model,
      initial,
      final,
      time,
      rate,
      factor: multiplier,
      discreteRate: multiplier - 1,
      continuousRate: Math.log(multiplier),
      characteristic: characteristicTime(input.model, rate),
      rules: rulesFor(input.model, rate),
      totalChangePercent: (final / initial - 1) * 100,
    },
  };
}

export interface ScheduleRow {
  time: number;
  amount: number;
  /** Percent change from the start. */
  changePercent: number;
}

/** The amount at the start, at whole periods, and at the end — at most about a dozen rows. */
export function growthSchedule(result: GrowthResult, rows = 12): ScheduleRow[] {
  const at = (t: number): ScheduleRow => {
    const amount =
      result.model === "discrete"
        ? result.initial * result.factor ** t
        : result.initial * Math.exp(result.continuousRate * t);
    return {
      time: t,
      amount,
      changePercent: (amount / result.initial - 1) * 100,
    };
  };
  const end = result.time;
  const step = end <= rows ? 1 : Math.max(1, niceStep(end, rows - 1));
  const times: number[] = [];
  for (let t = 0; t <= end + 1e-9 && times.length < rows + 2; t += step) {
    times.push(Number(t.toPrecision(12)));
  }
  if (end > 0 && Math.abs((times.at(-1) ?? 0) - end) > 1e-9) {
    times.push(end);
  }
  return times.map(at);
}

/** The amount after `t` periods for the chart. */
export function amountAt(result: GrowthResult, t: number): number {
  return result.initial * Math.exp(result.continuousRate * t);
}
